import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Boxes, Info } from "lucide-react";
import type { ProfileBlueprint, ProfilePluginRef, SettingsProfile } from "@/types";
import { SectionCard } from "@/components/SectionCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useBrainImages } from "@/api/queries/brainImages";
import { usePlugins } from "@/api/queries/plugins";
import { useConnectors } from "@/api/queries/connectors";
import { useSupportedModels } from "@/api/queries/models";
import { useUpdateSettingsProfile } from "@/api/mutations/settingsProfiles";
import { kannZuweisen } from "@/components/plugin-catalog/pluginGate";
import { CatalogSourceAlert } from "@/components/plugin-catalog/CatalogStatus";

// #281 (UC-3): Agenten-Ausstattung (Blaupause) am Profil — Brain-Image, Plugins (mit Scope),
// Connectoren, Defaults (Modell, Budgets). Speichern = EIN PATCH mit nur den geänderten Achsen
// (Patch-Semantik: key absent = unverändert; null/[] = bewusst leeren).
//
// Der Hinweis „gilt nur für NEUE Agents" ist keine Kosmetik: die Blaupause ist ein Snapshot beim
// Agent-Create — ohne den Satz weckte die Seite die Erwartung, bestehende Agents zögen mit.
const NONE = "__none__";

const EMPTY_BLUEPRINT: ProfileBlueprint = { brainImageId: null, plugins: [], connectors: [], defaults: {} };

function pluginKey(p: { marketplaceId: string; name?: string; pluginName?: string }): string {
  return `${p.marketplaceId}/${p.pluginName ?? p.name ?? ""}`;
}

function numOrNull(v: string): number | null | undefined {
  if (v.trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : undefined; // undefined = ungültig
}

export function BlueprintSection({ profile }: { profile: SettingsProfile }) {
  const current = profile.blueprint ?? EMPTY_BLUEPRINT; // Version-Skew: älterer Master ohne Feld
  const update = useUpdateSettingsProfile();
  const images = useBrainImages();
  const plugins = usePlugins();
  const connectors = useConnectors();
  const models = useSupportedModels();

  // BASELINE = der Server-Stand, von dem der lokale Entwurf abgeleitet ist. `dirty` vergleicht
  // IMMER gegen die Baseline (nicht gegen den neuesten Server-Stand) — sonst sähe eine fremde
  // Änderung wie ein eigener Entwurf aus. Weicht der Server von der Baseline ab, gibt es
  // entweder nichts zu schützen (nicht dirty → still übernehmen) oder einen Hinweis.
  const [baseline, setBaseline] = useState<ProfileBlueprint>(current);
  const [brainImageId, setBrainImageId] = useState<string | null>(current.brainImageId);
  const [pluginRefs, setPluginRefs] = useState<ProfilePluginRef[]>(current.plugins);
  const [connectorIds, setConnectorIds] = useState<string[]>(current.connectors.map((c) => c.connectorId));
  const [model, setModel] = useState<string | null>(current.defaults.model ?? null);
  const [monthly, setMonthly] = useState<string>(current.defaults.monthlyBudgetUsd?.toString() ?? "");
  const [turn, setTurn] = useState<string>(current.defaults.turnBudgetUsd?.toString() ?? "");

  const selectedPlugins = useMemo(() => new Map(pluginRefs.map((r) => [pluginKey(r), r])), [pluginRefs]);
  const activeImages = (images.data ?? []).filter((i) => i.active || i.id === brainImageId);
  // Nur freigeschaltete + im Manifest vorhandene Plugins sind zuweisbar (dieselbe Regel wie am Agent);
  // bereits gewählte bleiben sichtbar, damit eine verwaiste Referenz nicht unsichtbar wird.
  const assignablePlugins = (plugins.data?.plugins ?? []).filter(
    (p) => kannZuweisen({ ...p, enabled: selectedPlugins.has(pluginKey(p)) }),
  );
  // „Verwaist" nur gegen einen GELESENEN Katalog — Laden/Fehler/nicht lesbare Quelle ist
  // KEINE Aussage über die Referenz (Lesefehler ≠ entfernt, #287). Sonst räumte ein Admin bei
  // einer GitHub-Störung eine gültige Zuweisung ab.
  const unreadableMarkets = new Set((plugins.data?.sources ?? []).filter((s) => !s.ok).map((s) => s.marketplaceId));
  const orphanPluginRefs = plugins.isSuccess
    ? pluginRefs.filter((r) => !unreadableMarkets.has(r.marketplaceId) && !(plugins.data.plugins ?? []).some((p) => pluginKey(p) === pluginKey(r)))
    : [];
  // Nur, was NICHT ohnehin schon als Katalog-Zeile (status unknown) gerendert wird — sonst stünde
  // dieselbe Referenz doppelt in der Liste (gleicher React-Key).
  const unknownPluginRefs = pluginRefs.filter(
    (r) => unreadableMarkets.has(r.marketplaceId) && !assignablePlugins.some((p) => pluginKey(p) === pluginKey(r)),
  );
  const orphanConnectorIds = connectors.isSuccess ? connectorIds.filter((id) => !connectors.data.some((c) => c.id === id)) : [];
  const managedModelSet = profile.config.managed.model !== undefined;

  const monthlyVal = numOrNull(monthly);
  const turnVal = numOrNull(turn);
  const invalid = monthlyVal === undefined || turnVal === undefined;

  const sameRefs = (a: ProfilePluginRef[], b: ProfilePluginRef[]) =>
    a.length === b.length && a.every((r) => b.some((s) => pluginKey(s) === pluginKey(r) && s.scope === r.scope));
  const dirty =
    brainImageId !== baseline.brainImageId ||
    !sameRefs(pluginRefs, baseline.plugins) ||
    connectorIds.slice().sort().join() !== baseline.connectors.map((c) => c.connectorId).sort().join() ||
    (model ?? null) !== (baseline.defaults.model ?? null) ||
    (monthlyVal ?? null) !== (baseline.defaults.monthlyBudgetUsd ?? null) ||
    (turnVal ?? null) !== (baseline.defaults.turnBudgetUsd ?? null);

  // Weicht der Server von der Baseline ab (Refetch durch eine andere Sektion dieser Seite,
  // fremde Änderung), wird ohne Entwurf still nachgezogen; MIT Entwurf bleibt der Entwurf und der
  // Hinweis ist direkt aus dem Zustand ABGELEITET (kann nicht hängen bleiben).
  const serverKey = JSON.stringify(current);
  const serverChanged = serverKey !== JSON.stringify(baseline);
  useEffect(() => {
    if (serverChanged && !dirty) adopt(current);
  }, [serverChanged, dirty, current]);
  function adopt(bp: ProfileBlueprint) {
    setBaseline(bp);
    setBrainImageId(bp.brainImageId);
    setPluginRefs(bp.plugins);
    setConnectorIds(bp.connectors.map((c) => c.connectorId));
    setModel(bp.defaults.model ?? null);
    setMonthly(bp.defaults.monthlyBudgetUsd?.toString() ?? "");
    setTurn(bp.defaults.turnBudgetUsd?.toString() ?? "");
  }

  function togglePlugin(p: { marketplaceId: string; name: string }, on: boolean) {
    const key = pluginKey(p);
    setPluginRefs((prev) =>
      on
        ? [...prev.filter((r) => pluginKey(r) !== key), { marketplaceId: p.marketplaceId, pluginName: p.name, scope: "managed" }]
        : prev.filter((r) => pluginKey(r) !== key),
    );
  }
  function setScope(key: string, scope: "managed" | "owner") {
    setPluginRefs((prev) => prev.map((r) => (pluginKey(r) === key ? { ...r, scope } : r)));
  }

  function save() {
    if (invalid) return;
    // Nur geänderte Achsen mitschicken (Patch-Semantik) — sonst überschriebe ein veralteter Tab alles.
    const patch: Record<string, unknown> = {};
    if (brainImageId !== current.brainImageId) patch.brainImageId = brainImageId;
    if (!sameRefs(pluginRefs, current.plugins)) patch.plugins = pluginRefs;
    if (connectorIds.slice().sort().join() !== current.connectors.map((c) => c.connectorId).sort().join()) {
      patch.connectors = connectorIds.map((connectorId) => ({ connectorId }));
    }
    const defaults: Record<string, unknown> = {};
    if ((model ?? null) !== (current.defaults.model ?? null)) defaults.model = model;
    if ((monthlyVal ?? null) !== (current.defaults.monthlyBudgetUsd ?? null)) defaults.monthlyBudgetUsd = monthlyVal;
    if ((turnVal ?? null) !== (current.defaults.turnBudgetUsd ?? null)) defaults.turnBudgetUsd = turnVal;
    if (Object.keys(defaults).length > 0) patch.defaults = defaults;
    update.mutate(
      { id: profile.id, ...patch },
      {
        // Die Antwort IST der neue Server-Stand: Baseline darauf setzen, lokale Eingaben bleiben
        // (tippt jemand während des Roundtrips weiter, ist das ehrlich weiterhin „dirty").
        onSuccess: (saved) => {
          if (saved.blueprint) setBaseline(saved.blueprint);
          toast.success("Blaupause gespeichert — gilt für neu erstellte Agents.");
        },
        onError: (err) => toast.error(`Speichern fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  }

  return (
    <SectionCard testId="sp-blueprint">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-wider text-text3">
            <Boxes className="h-3.5 w-3.5" /> Agenten-Ausstattung (Blaupause)
          </div>
          <p className="mt-1 flex items-start gap-1.5 text-[12.5px] text-text2" data-testid="sp-blueprint-hint">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent-text" />
            <span>
              Gilt nur für <strong>neu</strong> erstellte Agents (Snapshot beim Anlegen). Bestehende Agents
              ändern sich nicht. Ein Connector wird dabei <em>aktiviert</em>, nicht <em>verbunden</em> —
              Zugangsdaten bleiben im Vault.
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          {serverChanged && (
            <span className="text-[12px] text-warn" data-testid="sp-blueprint-server-changed">
              Profil wurde zwischenzeitlich geändert —{" "}
              <button className="underline" onClick={() => adopt(current)}>Server-Stand laden</button> (verwirft deinen Entwurf)
            </span>
          )}
        <Button size="sm" onClick={save} disabled={!dirty || invalid || update.isPending} data-testid="sp-blueprint-save">
          {update.isPending ? "Speichert…" : "Blaupause speichern"}
        </Button>
        </div>
      </div>

      <div className="mt-4 grid gap-5 xl:grid-cols-2">
        {/* Brain-Image */}
        <div className="space-y-1.5">
          <Label>Brain-Image</Label>
          <Select value={brainImageId ?? NONE} onValueChange={(v) => setBrainImageId(v === NONE ? null : v)}>
            <SelectTrigger data-testid="sp-bp-image"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>— keins (Agent ohne Image, später setzbar)</SelectItem>
              {activeImages.map((i) => (
                <SelectItem key={i.id} value={i.id}>
                  {i.name}{!i.active ? " (inaktiv)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {brainImageId && !activeImages.some((i) => i.id === brainImageId) && images.data && (
            <p className="text-[12px] text-warn">Referenziertes Image ist nicht (mehr) im Katalog — der Agent-Create würde scheitern.</p>
          )}
        </div>

        {/* Defaults */}
        <div className="space-y-1.5">
          <Label>Modell (Admin-Fix)</Label>
          <Select value={model ?? NONE} onValueChange={(v) => setModel(v === NONE ? null : v)} disabled={managedModelSet}>
            <SelectTrigger data-testid="sp-bp-model"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>— kein Fix (Owner wählt)</SelectItem>
              {(models.data ?? []).map((m) => (
                <SelectItem key={m.value} value={m.value}>{m.displayName}</SelectItem>
              ))}
              {model && !(models.data ?? []).some((m) => m.value === model) && (
                <SelectItem value={model}>{model}</SelectItem>
              )}
            </SelectContent>
          </Select>
          {managedModelSet && (
            <p className="text-[12px] text-text3">
              Der Modell-Fix steht bereits im Managed-Layer (<code className="mono">model</code>) — genau eine Quelle.
            </p>
          )}
          <div className="grid grid-cols-2 gap-3 pt-1">
            <div className="space-y-1">
              <Label htmlFor="sp-bp-monthly">Monats-Budget (USD)</Label>
              <Input id="sp-bp-monthly" type="number" min={0} step="0.01" placeholder="Plattform-Default" value={monthly}
                onChange={(e) => setMonthly(e.target.value)} data-testid="sp-bp-monthly" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="sp-bp-turn">Per-Turn-Cap (USD)</Label>
              <Input id="sp-bp-turn" type="number" min={0} step="0.01" placeholder="Plattform-Default" value={turn}
                onChange={(e) => setTurn(e.target.value)} data-testid="sp-bp-turn" />
            </div>
          </div>
          {invalid && <p className="text-[12px] text-danger">Budgets müssen ≥ 0 sein (leer = Plattform-Default).</p>}
        </div>

        {/* Plugins */}
        <div className="space-y-1.5">
          <Label>Plugins ({pluginRefs.length})</Label>
          {plugins.isError && <p className="text-[12px] text-danger">Plugin-Katalog nicht ladbar — gewählte Plugins bleiben erhalten.</p>}
          <CatalogSourceAlert sources={plugins.data?.sources} />
          <ul className="max-h-64 divide-y divide-border overflow-auto rounded border border-border" data-testid="sp-bp-plugins">
            {assignablePlugins.length === 0 && !plugins.isLoading && (
              <li className="px-3 py-2 text-[12.5px] text-text3">Keine freigeschalteten Plugins im Katalog.</li>
            )}
            {assignablePlugins.map((p) => {
              const key = pluginKey(p);
              const sel = selectedPlugins.get(key);
              return (
                <li key={key} className="flex items-center gap-3 px-3 py-1.5 text-[13px]">
                  <input type="checkbox" className="h-3.5 w-3.5" checked={!!sel} onChange={(e) => togglePlugin(p, e.target.checked)}
                    data-testid={`sp-bp-plugin-${p.name}`} />
                  <span className="flex-1 truncate">
                    {p.name} <span className="text-text3">@{p.marketplaceName}</span>
                  </span>
                  {sel && (
                    <Select value={sel.scope} onValueChange={(v) => setScope(key, v as "managed" | "owner")}>
                      <SelectTrigger className="h-7 w-28 text-[12px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="managed">managed</SelectItem>
                        <SelectItem value="owner">owner</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </li>
              );
            })}
            {unknownPluginRefs.map((r) => (
              <li key={pluginKey(r)} className="flex items-center gap-3 px-3 py-1.5 text-[13px] text-text2">
                <input type="checkbox" className="h-3.5 w-3.5" checked onChange={() => togglePlugin({ marketplaceId: r.marketplaceId, name: r.pluginName }, false)} />
                <span className="flex-1 truncate">{r.pluginName} <span className="text-text3">@{r.marketplaceId}</span></span>
                <span className="text-[11.5px]">Quelle gerade nicht lesbar</span>
              </li>
            ))}
            {orphanPluginRefs.map((r) => (
              <li key={pluginKey(r)} className="flex items-center gap-3 px-3 py-1.5 text-[13px] text-warn">
                <input type="checkbox" className="h-3.5 w-3.5" checked onChange={() => togglePlugin({ marketplaceId: r.marketplaceId, name: r.pluginName }, false)} />
                <span className="flex-1 truncate">{r.pluginName} <span className="text-text3">@{r.marketplaceId}</span></span>
                <span className="text-[11.5px]">verwaist / nicht zuweisbar</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Connectoren */}
        <div className="space-y-1.5">
          <Label>Connectoren ({connectorIds.length})</Label>
          {connectors.isError && <p className="text-[12px] text-danger">Connector-Katalog nicht ladbar.</p>}
          <ul className="max-h-64 divide-y divide-border overflow-auto rounded border border-border" data-testid="sp-bp-connectors">
            {(connectors.data ?? []).length === 0 && !connectors.isLoading && (
              <li className="px-3 py-2 text-[12.5px] text-text3">Keine Connectoren im Katalog.</li>
            )}
            {(connectors.data ?? []).map((c) => (
              <li key={c.id} className="flex items-center gap-3 px-3 py-1.5 text-[13px]">
                <input type="checkbox" className="h-3.5 w-3.5" checked={connectorIds.includes(c.id)}
                  onChange={(e) => setConnectorIds((prev) => (e.target.checked ? [...prev.filter((x) => x !== c.id), c.id] : prev.filter((x) => x !== c.id)))}
                  data-testid={`sp-bp-connector-${c.id}`} />
                <span className="flex-1 truncate">{c.name} <span className="text-text3">· {c.transport}</span></span>
              </li>
            ))}
            {orphanConnectorIds.map((id) => (
              <li key={id} className="flex items-center gap-3 px-3 py-1.5 text-[13px] text-warn">
                <input type="checkbox" className="h-3.5 w-3.5" checked onChange={() => setConnectorIds((prev) => prev.filter((x) => x !== id))} />
                <span className="mono flex-1 truncate">{id}</span>
                <span className="text-[11.5px]">verwaist</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </SectionCard>
  );
}
